import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { CheckCircle } from 'lucide-react';

export function BidBreakdown({ bid }) {
  if (!bid) return null;

  const breakdown = bid.breakdown || [];
  const summary = bid.summary || {};
  const pricing = bid.pricing || {};

  return (
    <Card className="bg-gradient-to-br from-teal-50 to-emerald-50">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-2xl">Bid Calculation</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Created {new Date(bid.created_at).toLocaleDateString()}
            </p>
          </div>
          <Badge variant="outline" className="bg-white">
            {summary.profit_margin_pct}% Margin
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pricing Summary */}
        <div className="bg-white rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-baseline">
            <span className="text-lg font-semibold">Recommended Price</span>
            <span className="text-3xl font-bold text-brand tabular-nums">
              ${summary.recommended_monthly_price?.toFixed(2)}/mo
            </span>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Low</p>
              <p className="font-semibold tabular-nums">${pricing.low_price?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{pricing.low_markup_pct}% markup</p>
            </div>
            <div className="border-l border-r">
              <p className="text-xs text-muted-foreground mb-1">Mid (Target)</p>
              <p className="font-semibold text-brand tabular-nums">${pricing.mid_price?.toFixed(2)}</p>
              <p className="text-xs text-brand">{pricing.mid_markup_pct}% markup</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">High</p>
              <p className="font-semibold tabular-nums">${pricing.high_price?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{pricing.high_markup_pct}% markup</p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Monthly Hours</p>
            <p className="text-lg font-semibold tabular-nums">{summary.estimated_monthly_hours}</p>
          </div>
          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-muted-foreground">Monthly Profit</p>
            <p className="text-lg font-semibold text-success tabular-nums">
              ${summary.estimated_profit?.toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 md:col-span-1 col-span-2">
            <p className="text-xs text-muted-foreground">Price Range</p>
            <p className="text-sm font-semibold tabular-nums">{summary.price_range}</p>
          </div>
        </div>

        {/* Calculation Breakdown */}
        <div className="bg-white rounded-lg p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-brand" />
            How This Bid Was Calculated
          </h3>
          <div className="space-y-1 text-sm">
            {breakdown.map((line, index) => (
              <p 
                key={index} 
                className={`${line.startsWith('\\n') ? 'mt-3 font-semibold' : ''} ${line.includes(':') ? '' : 'text-muted-foreground'}`}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {line.replace(/\\n/g, '')}
              </p>
            ))}
          </div>
        </div>

        {/* Inputs Summary */}
        <div className="bg-white rounded-lg p-4">
          <h3 className="font-semibold mb-2">Inputs</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Building:</span>{' '}
              <span className="font-medium">{bid.inputs?.building_type?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Condition:</span>{' '}
              <span className="font-medium capitalize">{bid.inputs?.condition}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Square Footage:</span>{' '}
              <span className="font-medium tabular-nums">{bid.inputs?.square_footage?.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Frequency:</span>{' '}
              <span className="font-medium">{bid.inputs?.frequency?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Restrooms:</span>{' '}
              <span className="font-medium tabular-nums">{bid.inputs?.num_restrooms}</span>
            </div>
            {bid.inputs?.num_offices > 0 && (
              <div>
                <span className="text-muted-foreground">Offices:</span>{' '}
                <span className="font-medium tabular-nums">{bid.inputs?.num_offices}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
